import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Pencil, Plus, Copy, Link as LinkIcon, X } from "lucide-react";

const FRONTEND_ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

const emptyStore = {
    name: "",
    address: "",
    city: "",
    region: "",
    country: "Italia",
    cap: "",
    phone: "",
    notification_email: "",
    google_maps_url: "",
    header_image_url: "",
    contact_person: "",
    support_phone: "",
    notes: "",
    active: true,
    slug: "",
    product_ids: [],
};

const emptyAvailability = {
    weekly: Array.from({ length: 7 }, (_, i) => ({
        day_of_week: i,
        enabled: i < 6,
        ranges: [{ open_time: "10:00", close_time: "19:00" }],
    })),
    slot_duration_minutes: 45,
    slot_interval_minutes: 60,
    max_per_slot: 1,
    blocked_dates: [],
};

// Normalize incoming availability (legacy open_time/close_time → ranges array)
function normalizeAvailability(av) {
    if (!av) return emptyAvailability;
    const weekly = (av.weekly || []).map((w) => {
        let ranges = w.ranges;
        if (!Array.isArray(ranges) || ranges.length === 0) {
            ranges = [{ open_time: w.open_time || "10:00", close_time: w.close_time || "19:00" }];
        }
        return {
            day_of_week: w.day_of_week,
            enabled: w.enabled !== false,
            ranges,
        };
    });
    return {
        ...emptyAvailability,
        ...av,
        weekly: weekly.length === 7 ? weekly : emptyAvailability.weekly,
    };
}

const dayNames = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];

export default function AdminStores() {
    const [stores, setStores] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyStore);
    const [availability, setAvailability] = useState(emptyAvailability);
    const [blockedDateInput, setBlockedDateInput] = useState("");
    const [saving, setSaving] = useState(false);
    const [products, setProducts] = useState([]);

    const refresh = async () => {
        const { data } = await api.get("/admin/stores");
        setStores(data);
    };

    useEffect(() => {
        refresh();
        api.get("/admin/products").then((r) => setProducts(r.data || [])).catch(() => setProducts([]));
    }, []);

    const openNew = () => {
        setEditing(null);
        setForm(emptyStore);
        setAvailability(emptyAvailability);
        setOpen(true);
    };

    const openEdit = async (s) => {
        setEditing(s);
        setForm({ ...emptyStore, ...s });
        try {
            const { data } = await api.get(`/admin/stores/${s.id}/availability`);
            setAvailability(normalizeAvailability(data));
        } catch {
            setAvailability(emptyAvailability);
        }
        setOpen(true);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            let saved;
            if (editing) {
                const { data } = await api.put(`/admin/stores/${editing.id}`, form);
                saved = data;
            } else {
                const { data } = await api.post("/admin/stores", form);
                saved = data;
            }
            await api.put(`/admin/stores/${saved.id}/availability`, availability);
            toast.success(editing ? "Negozio aggiornato" : "Negozio creato");
            setOpen(false);
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (s) => {
        const msg =
            `ELIMINA DEFINITIVAMENTE il negozio "${s.name}"?\n\n` +
            `Verranno cancellati anche TUTTE le prenotazioni, i feedback e la disponibilità di questo negozio.\n` +
            `Gli account rivenditore associati saranno scollegati (o eliminati se restano senza negozi).\n\n` +
            `Questa operazione è irreversibile.`;
        if (!window.confirm(msg)) return;
        try {
            const { data } = await api.delete(`/admin/stores/${s.id}`);
            const d = data.deleted || {};
            toast.success(
                `Negozio eliminato. Rimosse ${d.bookings || 0} prenotazioni, ${d.feedback || 0} feedback.`,
                { duration: 6000 },
            );
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const copyLink = (slug) => {
        const url = `${FRONTEND_ORIGIN}/negozio/${slug}`;
        navigator.clipboard.writeText(url);
        toast.success("Link copiato negli appunti");
    };

    const addBlockedDate = () => {
        if (!blockedDateInput) return;
        if (!availability.blocked_dates.includes(blockedDateInput)) {
            setAvailability({
                ...availability,
                blocked_dates: [...availability.blocked_dates, blockedDateInput].sort(),
            });
        }
        setBlockedDateInput("");
    };

    return (
        <div>
            <Toaster position="top-center" richColors />
            <div className="flex items-center justify-between mb-8">
                <div>
                    <div className="overline mb-2">Backoffice</div>
                    <h1 className="font-serif-display text-3xl sm:text-4xl text-magni-navy">
                        Gestione negozi
                    </h1>
                </div>
                <button
                    data-testid="add-store-btn"
                    onClick={openNew}
                    className="magni-btn-primary"
                >
                    <Plus className="w-4 h-4 mr-2" /> Nuovo negozio
                </button>
            </div>

            <div className="bg-white border border-magni-light">
                <table className="w-full text-sm">
                    <thead className="bg-magni-cream text-magni-muted">
                        <tr>
                            <th className="text-left px-6 py-3 font-medium">Nome</th>
                            <th className="text-left px-6 py-3 font-medium">Città</th>
                            <th className="text-left px-6 py-3 font-medium">Slug / Link ADV</th>
                            <th className="text-left px-6 py-3 font-medium">Email rivenditore</th>
                            <th className="text-left px-6 py-3 font-medium">Stato</th>
                            <th className="px-6 py-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {stores.length === 0 && (
                            <tr>
                                <td colSpan="6" className="px-6 py-10 text-center text-magni-muted">
                                    Nessun negozio. Aggiungine uno per iniziare.
                                </td>
                            </tr>
                        )}
                        {stores.map((s) => (
                            <tr key={s.id} className="border-t border-magni-light" data-testid={`store-row-${s.slug}`}>
                                <td className="px-6 py-3 text-magni-navy font-medium">{s.name}</td>
                                <td className="px-6 py-3 text-magni-muted">
                                    {s.city}
                                    {s.region ? ` · ${s.region}` : ""}
                                </td>
                                <td className="px-6 py-3">
                                    <div className="flex items-center gap-2">
                                        <code className="text-xs bg-magni-light px-2 py-1">/negozio/{s.slug}</code>
                                        <button
                                            data-testid={`copy-link-${s.slug}`}
                                            onClick={() => copyLink(s.slug)}
                                            className="text-magni-muted hover:text-magni-navy"
                                            title="Copia link"
                                        >
                                            <Copy className="w-4 h-4" />
                                        </button>
                                        <a
                                            href={`/negozio/${s.slug}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-magni-muted hover:text-magni-navy"
                                            title="Apri"
                                        >
                                            <LinkIcon className="w-4 h-4" />
                                        </a>
                                    </div>
                                </td>
                                <td className="px-6 py-3 text-magni-muted text-xs">
                                    <div>{s.notification_email}</div>
                                    {s.contact_person && (
                                        <div className="mt-1">
                                            <span className="text-magni-navy/70">Referente:</span>{" "}
                                            {s.contact_person}
                                        </div>
                                    )}
                                </td>
                                <td className="px-6 py-3">
                                    <span
                                        className={`text-xs font-semibold uppercase tracking-wider px-2 py-1 ${
                                            s.active
                                                ? "bg-green-50 text-green-700"
                                                : "bg-red-50 text-red-700"
                                        }`}
                                    >
                                        {s.active ? "attivo" : "disattivato"}
                                    </span>
                                </td>
                                <td className="px-6 py-3 text-right">
                                    <button
                                        data-testid={`edit-store-${s.slug}`}
                                        onClick={() => openEdit(s)}
                                        className="text-magni-navy hover:text-magni-navy inline-flex items-center gap-1 text-xs uppercase tracking-wider font-semibold"
                                    >
                                        <Pencil className="w-3 h-3" /> Modifica
                                    </button>
                                    {s.active && (
                                        <button
                                            data-testid={`delete-store-${s.slug}`}
                                            onClick={() => handleDelete(s)}
                                            className="ml-4 text-red-600 hover:text-red-800 text-xs uppercase tracking-wider font-semibold"
                                        >
                                            Elimina
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Modal */}
            {open && (
                <div
                    className="fixed inset-0 bg-black/50 z-50 overflow-y-auto flex items-start justify-center py-8 px-4"
                    onClick={() => setOpen(false)}
                >
                    <div
                        data-testid="store-edit-modal"
                        className="bg-white max-w-3xl w-full border border-magni-light my-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-8 py-5 border-b border-magni-light">
                            <div>
                                <div className="overline">{editing ? "Modifica" : "Nuovo"}</div>
                                <h2 className="font-serif-display text-2xl text-magni-navy">
                                    {editing ? form.name : "Crea negozio"}
                                </h2>
                            </div>
                            <button onClick={() => setOpen(false)} className="text-magni-muted hover:text-magni-navy">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-8 space-y-8 max-h-[70vh] overflow-y-auto">
                            <div>
                                <div className="overline mb-4">Dati negozio</div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <FieldInput
                                        label="Nome *"
                                        value={form.name}
                                        onChange={(v) => setForm({ ...form, name: v })}
                                        tid="store-name"
                                    />
                                    <FieldInput
                                        label="Slug (URL)"
                                        value={form.slug}
                                        onChange={(v) => setForm({ ...form, slug: v })}
                                        placeholder="auto dal nome"
                                    />
                                    <FieldInput
                                        label="Indirizzo *"
                                        value={form.address}
                                        onChange={(v) => setForm({ ...form, address: v })}
                                    />
                                    <FieldInput
                                        label="Città *"
                                        value={form.city}
                                        onChange={(v) => setForm({ ...form, city: v })}
                                    />
                                    <FieldInput
                                        label="Regione"
                                        value={form.region}
                                        onChange={(v) => setForm({ ...form, region: v })}
                                    />
                                    <FieldInput
                                        label="Nazione"
                                        value={form.country}
                                        onChange={(v) => setForm({ ...form, country: v })}
                                        placeholder="Italia"
                                    />
                                    <FieldInput
                                        label="CAP"
                                        value={form.cap}
                                        onChange={(v) => setForm({ ...form, cap: v })}
                                        placeholder="20100"
                                    />
                                    <FieldInput
                                        label="Telefono *"
                                        value={form.phone}
                                        onChange={(v) => setForm({ ...form, phone: v })}
                                    />
                                    <FieldInput
                                        label="Email notifica rivenditore *"
                                        value={form.notification_email}
                                        onChange={(v) => setForm({ ...form, notification_email: v })}
                                        type="email"
                                    />
                                    <FieldInput
                                        label="URL Google Maps"
                                        value={form.google_maps_url}
                                        onChange={(v) => setForm({ ...form, google_maps_url: v })}
                                    />
                                </div>
                                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <FieldInput
                                        label="Referente negozio (nome cognome)"
                                        value={form.contact_person}
                                        onChange={(v) => setForm({ ...form, contact_person: v })}
                                        placeholder="Es. Mario Rossi"
                                    />
                                    <FieldInput
                                        label="Telefono assistenza clienti"
                                        value={form.support_phone}
                                        onChange={(v) => setForm({ ...form, support_phone: v })}
                                        placeholder="+39 ..."
                                    />
                                </div>

                                {/* Products picker */}
                                <div className="mt-6">
                                    <label className="text-xs uppercase tracking-[0.15em] font-semibold text-magni-navy block mb-2">
                                        Prodotti provabili in questo negozio
                                    </label>
                                    {products.length === 0 ? (
                                        <div className="text-xs text-magni-muted bg-magni-cream border border-magni-light p-3">
                                            Nessun prodotto nel catalogo. Aggiungili dalla sezione{" "}
                                            <a href="/admin/prodotti" className="text-magni-navy underline">Prodotti</a>.
                                        </div>
                                    ) : (
                                        <div className="flex flex-wrap gap-2">
                                            {products.map((p) => {
                                                const selected = (form.product_ids || []).includes(p.id);
                                                return (
                                                    <button
                                                        type="button"
                                                        key={p.id}
                                                        data-testid={`product-toggle-${p.id}`}
                                                        onClick={() => {
                                                            const ids = new Set(form.product_ids || []);
                                                            if (selected) ids.delete(p.id);
                                                            else ids.add(p.id);
                                                            setForm({ ...form, product_ids: Array.from(ids) });
                                                        }}
                                                        className="text-xs px-3 py-2 border transition-colors"
                                                        style={{
                                                            background: selected ? "#174860" : "#fff",
                                                            color: selected ? "#fff" : "#174860",
                                                            borderColor: "#174860",
                                                        }}
                                                    >
                                                        {p.name}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                <div className="mt-4">
                                    <label className="text-xs uppercase tracking-[0.15em] font-semibold text-magni-navy block mb-2">
                                        Note interne (visibili solo nel backoffice)
                                    </label>
                                    <textarea
                                        rows={2}
                                        value={form.notes}
                                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                        placeholder="Es. orari festivi, contatti riferimento, particolarità del punto vendita..."
                                        className="magni-input"
                                    />
                                </div>
                                <div className="mt-4">
                                    <label className="text-xs uppercase tracking-[0.15em] font-semibold text-magni-navy block mb-2">
                                        URL immagine header (consigliato: 1920×800, sfondo bianco/chiaro)
                                    </label>
                                    <input
                                        data-testid="store-header-image-url"
                                        type="url"
                                        value={form.header_image_url}
                                        onChange={(e) =>
                                            setForm({ ...form, header_image_url: e.target.value })
                                        }
                                        placeholder="https://..."
                                        className="magni-input"
                                    />
                                    {form.header_image_url && (
                                        <div className="mt-3 border border-magni-light bg-magni-cream p-2">
                                            <img
                                                src={form.header_image_url}
                                                alt="Anteprima header"
                                                className="w-full h-32 object-cover"
                                                onError={(e) => {
                                                    e.target.style.display = "none";
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                                <label className="flex items-center gap-2 mt-4 text-sm cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={form.active}
                                        onChange={(e) => setForm({ ...form, active: e.target.checked })}
                                        className="w-4 h-4 accent-[#174860]"
                                    />
                                    <span className="text-magni-navy">Negozio attivo</span>
                                </label>
                            </div>

                            <div>
                                <div className="overline mb-2">Disponibilità settimanale</div>
                                <p className="text-xs text-magni-muted mb-3">
                                    Aggiungi più fasce orarie per coprire la chiusura pranzo (es. 10:00–13:00 e 15:30–19:30).
                                </p>
                                <div className="space-y-2">
                                    {availability.weekly.map((w, idx) => (
                                        <div
                                            key={w.day_of_week}
                                            className="bg-magni-cream border border-magni-light p-3"
                                            data-testid={`day-row-${w.day_of_week}`}
                                        >
                                            <div className="flex flex-wrap items-center gap-3 mb-2">
                                                <label className="flex items-center gap-2 w-32 text-sm font-medium">
                                                    <input
                                                        type="checkbox"
                                                        checked={w.enabled}
                                                        onChange={(e) => {
                                                            const next = [...availability.weekly];
                                                            next[idx] = { ...w, enabled: e.target.checked };
                                                            setAvailability({ ...availability, weekly: next });
                                                        }}
                                                        className="w-4 h-4 accent-[#174860]"
                                                    />
                                                    {dayNames[w.day_of_week]}
                                                </label>
                                                {w.enabled && (
                                                    <button
                                                        type="button"
                                                        data-testid={`add-range-${w.day_of_week}`}
                                                        onClick={() => {
                                                            const next = [...availability.weekly];
                                                            next[idx] = {
                                                                ...w,
                                                                ranges: [...(w.ranges || []), { open_time: "15:30", close_time: "19:30" }],
                                                            };
                                                            setAvailability({ ...availability, weekly: next });
                                                        }}
                                                        className="text-xs uppercase tracking-[0.1em] font-semibold text-magni-navy hover:underline ml-auto"
                                                    >
                                                        + Aggiungi fascia
                                                    </button>
                                                )}
                                            </div>
                                            {w.enabled &&
                                                (w.ranges || []).map((rng, rIdx) => (
                                                    <div
                                                        key={rIdx}
                                                        className="flex flex-wrap items-center gap-2 mt-2 ml-0 sm:ml-32"
                                                        data-testid={`range-row-${w.day_of_week}-${rIdx}`}
                                                    >
                                                        <input
                                                            type="time"
                                                            value={rng.open_time}
                                                            onChange={(e) => {
                                                                const next = [...availability.weekly];
                                                                const newRanges = [...(w.ranges || [])];
                                                                newRanges[rIdx] = { ...rng, open_time: e.target.value };
                                                                next[idx] = { ...w, ranges: newRanges };
                                                                setAvailability({ ...availability, weekly: next });
                                                            }}
                                                            className="magni-input max-w-[120px]"
                                                            style={{ padding: "8px 10px" }}
                                                        />
                                                        <span className="text-magni-muted">→</span>
                                                        <input
                                                            type="time"
                                                            value={rng.close_time}
                                                            onChange={(e) => {
                                                                const next = [...availability.weekly];
                                                                const newRanges = [...(w.ranges || [])];
                                                                newRanges[rIdx] = { ...rng, close_time: e.target.value };
                                                                next[idx] = { ...w, ranges: newRanges };
                                                                setAvailability({ ...availability, weekly: next });
                                                            }}
                                                            className="magni-input max-w-[120px]"
                                                            style={{ padding: "8px 10px" }}
                                                        />
                                                        {(w.ranges || []).length > 1 && (
                                                            <button
                                                                type="button"
                                                                data-testid={`remove-range-${w.day_of_week}-${rIdx}`}
                                                                onClick={() => {
                                                                    const next = [...availability.weekly];
                                                                    const newRanges = (w.ranges || []).filter((_, i) => i !== rIdx);
                                                                    next[idx] = { ...w, ranges: newRanges };
                                                                    setAvailability({ ...availability, weekly: next });
                                                                }}
                                                                className="text-magni-muted hover:text-red-600 text-lg leading-none"
                                                                title="Rimuovi fascia"
                                                            >
                                                                ×
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                        </div>
                                    ))}
                                </div>
                                <div className="grid grid-cols-3 gap-4 mt-4">
                                    <FieldInput
                                        label="Durata slot (min)"
                                        value={availability.slot_duration_minutes}
                                        type="number"
                                        onChange={(v) =>
                                            setAvailability({
                                                ...availability,
                                                slot_duration_minutes: parseInt(v, 10) || 45,
                                            })
                                        }
                                    />
                                    <FieldInput
                                        label="Intervallo tra slot (min)"
                                        value={availability.slot_interval_minutes}
                                        type="number"
                                        onChange={(v) =>
                                            setAvailability({
                                                ...availability,
                                                slot_interval_minutes: parseInt(v, 10) || 60,
                                            })
                                        }
                                    />
                                    <FieldInput
                                        label="Max prenotazioni per slot"
                                        value={availability.max_per_slot}
                                        type="number"
                                        onChange={(v) =>
                                            setAvailability({
                                                ...availability,
                                                max_per_slot: parseInt(v, 10) || 1,
                                            })
                                        }
                                    />
                                </div>
                            </div>

                            <div>
                                <div className="overline mb-4">Date bloccate (festività/chiusure)</div>
                                <div className="flex items-center gap-2 mb-3">
                                    <input
                                        type="date"
                                        value={blockedDateInput}
                                        onChange={(e) => setBlockedDateInput(e.target.value)}
                                        className="magni-input max-w-[200px]"
                                    />
                                    <button onClick={addBlockedDate} className="magni-btn-secondary" style={{ padding: "8px 16px" }}>
                                        Aggiungi
                                    </button>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {availability.blocked_dates.map((d) => (
                                        <span
                                            key={d}
                                            className="bg-magni-light text-magni-navy text-xs px-3 py-1 flex items-center gap-2"
                                        >
                                            {d}
                                            <button
                                                onClick={() =>
                                                    setAvailability({
                                                        ...availability,
                                                        blocked_dates: availability.blocked_dates.filter((x) => x !== d),
                                                    })
                                                }
                                                className="text-magni-muted hover:text-red-600"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {editing && (
                                <StoreAccountsSection storeId={editing.id} />
                            )}
                            {!editing && (
                                <div className="bg-magni-cream border border-magni-light p-4 text-xs text-magni-muted">
                                    Salva prima il negozio per poter creare un account di accesso rivenditore.
                                </div>
                            )}
                        </div>

                        <div className="px-8 py-4 border-t border-magni-light flex justify-end gap-3">
                            <button onClick={() => setOpen(false)} className="magni-btn-ghost">
                                Annulla
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                data-testid="save-store-btn"
                                className="magni-btn-primary"
                            >
                                {saving ? "Salvataggio..." : "Salva"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function FieldInput({ label, value, onChange, type = "text", placeholder = "", tid }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-[0.15em] font-semibold text-magni-navy block mb-2">
                {label}
            </label>
            <input
                data-testid={tid}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="magni-input"
            />
        </div>
    );
}

function StoreAccountsSection({ storeId }) {
    const [accounts, setAccounts] = useState([]);
    const [newEmail, setNewEmail] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [newName, setNewName] = useState("");
    const [busy, setBusy] = useState(false);

    const refresh = async () => {
        const { data } = await api.get(`/admin/stores/${storeId}/accounts`);
        setAccounts(data);
    };

    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeId]);

    const handleCreate = async () => {
        if (!newEmail) {
            toast.error("Inserisci almeno l'email");
            return;
        }
        if (newPassword && newPassword.length < 6) {
            toast.error("La password deve avere almeno 6 caratteri");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/stores/${storeId}/accounts`, {
                email: newEmail,
                password: newPassword || undefined,
                name: newName,
            });
            if (data.linked) {
                toast.success(`Account esistente ${newEmail} collegato anche a questo negozio (${data.store_ids.length} negozi totali)`);
            } else {
                toast.success(`Account creato per ${newEmail}`);
            }
            setNewEmail("");
            setNewPassword("");
            setNewName("");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const handleUnlink = async (acc) => {
        const isOnlyStore = (acc.store_ids || []).length <= 1;
        const msg = isOnlyStore
            ? `Questo è l'unico negozio collegato a ${acc.email}. Confermando l'account verrà ELIMINATO. Procedere?`
            : `Scollegare ${acc.email} da questo negozio? L'account resta valido per gli altri ${(acc.store_ids || []).length - 1} negozi.`;
        if (!window.confirm(msg)) return;
        try {
            await api.delete(`/admin/stores/${storeId}/accounts/${acc.id}`);
            toast.success(isOnlyStore ? "Account eliminato" : "Account scollegato");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const handleResetPassword = async (acc) => {
        const pw = window.prompt(`Nuova password per ${acc.email} (min 6 caratteri):`);
        if (!pw) return;
        if (pw.length < 6) {
            toast.error("Minimo 6 caratteri");
            return;
        }
        try {
            await api.post(`/admin/accounts/${acc.id}/reset-password`, { password: pw });
            toast.success("Password aggiornata");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    return (
        <div data-testid="store-accounts-section">
            <div className="overline mb-2">Account accesso rivenditore</div>
            <p className="text-xs text-magni-muted mb-4">
                Gli account creati qui possono accedere a <code>/rivenditore</code> e vedere
                solo le prenotazioni di questo negozio. <strong>Un singolo account può gestire più negozi:</strong> se inserisci un'email già usata altrove, l'account esistente viene <em>collegato</em> a questo negozio (puoi lasciare vuota la password in quel caso).
            </p>

            <div className="bg-magni-cream border border-magni-light p-4 mb-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <input
                        type="email"
                        placeholder="email@negozio.it"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        className="magni-input"
                        data-testid="new-account-email"
                    />
                    <input
                        type="text"
                        placeholder="Nome (es. Mario Rossi)"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        className="magni-input"
                    />
                    <input
                        type="text"
                        placeholder="Password (min 6 — vuoto se collega esistente)"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="magni-input"
                        data-testid="new-account-password"
                    />
                </div>
                <button
                    onClick={handleCreate}
                    disabled={busy}
                    className="magni-btn-primary mt-3"
                    data-testid="create-account-btn"
                    style={{ padding: "10px 20px", fontSize: "12px" }}
                >
                    {busy ? "Creazione..." : "Crea o collega account"}
                </button>
            </div>

            {accounts.length === 0 ? (
                <div className="text-sm text-magni-muted">Nessun account creato.</div>
            ) : (
                <div className="border border-magni-light bg-white">
                    {accounts.map((a) => {
                        const storeCount = (a.store_ids || []).length || 1;
                        return (
                            <div
                                key={a.id}
                                className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-magni-light last:border-b-0"
                            >
                                <div>
                                    <div className="text-sm font-medium text-magni-navy flex items-center gap-2">
                                        {a.email}
                                        {storeCount > 1 && (
                                            <span className="text-[10px] bg-magni-celeste text-magni-navy px-2 py-0.5 tracking-wider uppercase font-semibold">
                                                gestisce {storeCount} negozi
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-magni-muted">{a.name || "—"}</div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={() => handleResetPassword(a)}
                                        className="text-xs uppercase tracking-wider font-semibold text-magni-navy hover:opacity-75"
                                    >
                                        Reset password
                                    </button>
                                    <button
                                        onClick={() => handleUnlink(a)}
                                        className="text-xs uppercase tracking-wider font-semibold text-red-600 hover:text-red-800"
                                    >
                                        {storeCount > 1 ? "Scollega da qui" : "Elimina"}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

